import { InfoOutlined } from '@mui/icons-material';
import {
  Box,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import useComponentSize from '@rehooks/component-size';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { AsyncContent } from '~/components/AsyncContent';
import { TooltippedTruncatedText } from '~/components/TooltippedTruncatedText';

import { QuickLinkViewItem } from '../../models';

import { useSxStyles } from './useStyles';

export interface Props {
  quickLinks?: QuickLinkViewItem[];
  isLoadingQuickLinks: boolean;
  failedToLoadQuickLinks: boolean;
  onNoOfFittingListItemsChange?: (noOfFittingListItems: number) => void;
  showFullDescription: boolean;
  isMenuListed?: boolean;
}

export const QuickLinksItemsList: React.FC<Props> = ({
  quickLinks,
  isLoadingQuickLinks,
  failedToLoadQuickLinks,
  onNoOfFittingListItemsChange,
  showFullDescription,
  isMenuListed,
}) => {
  const { t } = useTranslation();
  const sxStyles = useSxStyles();

  const containerRef = React.useRef<HTMLDivElement>(null);
  const { height: containerHeight } = useComponentSize(containerRef);
  const pageLocation = window.location.origin;

  React.useEffect(() => {
    const noOfFittingListItems = containerHeight / 29;
    if (onNoOfFittingListItemsChange) {
      onNoOfFittingListItemsChange(noOfFittingListItems);
    }
  }, [containerHeight, onNoOfFittingListItemsChange]);

  return (
    <Box
      ref={containerRef}
      sx={{
        ...sxStyles.refDiv,
        ...(isMenuListed ? sxStyles.refDivIsMenuListed : {}),
      }}
    >
      <AsyncContent
        isLoading={isLoadingQuickLinks}
        failedToLoad={failedToLoadQuickLinks}
      >
        <Box
          data-cy="list"
          sx={{
            ...sxStyles.list,
            ...(isMenuListed ? sxStyles.listIsMenuListed : {}),
          }}
        >
          {quickLinks?.length ? (
            <TableContainer
              sx={{
                ...sxStyles.tableContainer,
                ...(isMenuListed ? sxStyles.tableContainerIsMenuListed : {}),
              }}
            >
              <Table>
                <colgroup>
                  <col
                    style={{
                      width: showFullDescription ? '50%' : 'calc(100% - 32px)',
                    }}
                  />
                  {showFullDescription ? (
                    <col
                      style={{
                        width: '50%',
                      }}
                    />
                  ) : (
                    <col
                      style={{
                        width: '32px',
                      }}
                    />
                  )}
                </colgroup>
                {showFullDescription && (
                  <TableHead>
                    <TableRow>
                      <TableCell sx={sxStyles.head}>{t('links')}</TableCell>
                      <TableCell sx={sxStyles.head}>
                        {t('description')}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                )}
                <TableBody>
                  {quickLinks.map((link) => (
                    <TableRow data-cy="list-item" key={link.id}>
                      <TableCell
                        sx={{
                          ...sxStyles.cell,
                          ...(isMenuListed ? sxStyles.isMenuListedCell : {}),
                        }}
                      >
                        <Link
                          {...(link.url.startsWith(pageLocation)
                            ? {}
                            : { target: '_blank' })}
                          color="primary"
                          href={link.url}
                          sx={{
                            ...(isMenuListed ? sxStyles.isMenuListed : {}),
                          }}
                        >
                          <TooltippedTruncatedText dataCy="name">
                            {link.name}
                          </TooltippedTruncatedText>
                        </Link>
                      </TableCell>
                      {showFullDescription ? (
                        <TableCell sx={sxStyles.cell}>
                          <TooltippedTruncatedText dataCy="description">
                            {link.description || ''}
                          </TooltippedTruncatedText>
                        </TableCell>
                      ) : (
                        <TableCell sx={sxStyles.cellIcon}>
                          {link.description && (
                            <Tooltip title={link.description}>
                              <Box sx={sxStyles.iconContainer}>
                                <InfoOutlined
                                  sx={sxStyles.icon}
                                  data-cy="icon"
                                />
                              </Box>
                            </Tooltip>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <></>
          )}
        </Box>
      </AsyncContent>
    </Box>
  );
};
